import { api } from './authService';

const BASE = '/api/v2/offer_reports/studio';

export const BASELINE_OPTIONS = [
  { id: 'universe', label: 'Universe', short: 'universe' },
  { id: 'clickers', label: 'Offer clickers', short: 'clickers' },
  { id: 'vertical', label: 'Vertical converters', short: 'vertical' },
];

export const LAST_OFFER_KEY = 'cube.offerReports.lastOffer';

export function readLastOffer() {
  try {
    return window.localStorage.getItem(LAST_OFFER_KEY) || '';
  } catch {
    return '';
  }
}

export function writeLastOffer(offerId) {
  try {
    if (offerId) window.localStorage.setItem(LAST_OFFER_KEY, String(offerId));
  } catch {
    /* ignore */
  }
}

/** Encode explore rules for the URL (`?rules=`) and the segment deep link. */
export function encodeRules(rules) {
  if (!rules || rules.length === 0) return '';
  const json = JSON.stringify(rules);
  const b64 = window.btoa(unescape(encodeURIComponent(json)));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeRules(encoded) {
  if (!encoded) return [];
  try {
    const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '==='.slice((b64.length + 3) % 4);
    const json = decodeURIComponent(escape(window.atob(padded)));
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Build a segment-builder condition from a (feature, value) finding. */
/**
 * `value.segment_condition` (from the API) carries the exact profile column,
 * operator and translated value(s) when the feature maps to one; otherwise
 * the rule keeps the label + original value and the editor asks for a column.
 */
export function ruleFromFinding(feature, value) {
  const sc = value.segment_condition || null;
  const under = value.index != null && value.index < 100;
  const stored = under ? value.exclude_rule : value.include_rule;
  const label = under ? value.exclude_label : value.include_label;
  if (stored && typeof stored === 'object') {
    const stamped = {
      ...stored,
      offer_feature: stored.offer_feature || feature.feature,
      offer_value: stored.offer_value ?? value.value,
      offer_value_label: stored.offer_value_label || value.value_label,
      offer_direction: under ? 'under' : 'over',
    };
    if (stamped.kind === 'group' && label && !stamped.display_name) {
      stamped.display_name = label;
    } else if (stamped.kind !== 'group' && label && !stamped.display_name) {
      stamped.display_name = label;
    }
    return stamped;
  }
  return {
    kind: 'condition',
    catalog: sc?.catalog || '',
    schema: sc?.schema || '',
    table: sc?.table || '',
    column: sc?.column || '',
    column_type: '',
    has_value_list: false,
    display_name: feature.display_label,
    operator: sc?.operator || (under ? 'neq' : 'eq'),
    value: sc ? sc.value : value.value,
    value2: sc?.value2 || '',
    folder_list: [],
    offer_feature: feature.feature,
    offer_value: value.value,
    offer_value_label: value.value_label,
    offer_direction: under ? 'under' : 'over',
  };
}

export const RULE_OP_SYMBOL = {
  eq: '=',
  neq: '≠',
  in: 'in',
  not_in: 'not in',
  between: 'between',
  is_null: 'is empty',
  is_not_null: 'is not empty',
};

export function ruleKey(rule) {
  return `${rule.offer_feature || rule.column}::${rule.offer_value ?? rule.value}`;
}

export const offerStudioService = {
  async listOffers(organizationId) {
    const res = await api.get(`${BASE}/offers`, { params: { organization_id: organizationId } });
    return res.data || [];
  },
  async overview(offerId, organizationId, baseline) {
    const res = await api.get(`${BASE}/${offerId}/overview`, {
      params: { organization_id: organizationId, baseline },
    });
    return res.data;
  },
  async features(offerId, organizationId, baseline, category) {
    const res = await api.get(`${BASE}/${offerId}/features`, {
      params: { organization_id: organizationId, baseline, category: category || undefined },
    });
    return res.data || [];
  },
  async insights(offerId, organizationId, baseline, limit = 12) {
    const res = await api.get(`${BASE}/${offerId}/insights`, {
      params: { organization_id: organizationId, baseline, limit },
    });
    return res.data || [];
  },
  async segmentSuggestions(offerId, organizationId, baseline, limit = 10) {
    const res = await api.get(`${BASE}/${offerId}/segment-suggestions`, {
      params: { organization_id: organizationId, baseline, limit },
    });
    return res.data || [];
  },
};
